import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { CurrencyExchange, Box } from "@ui-kit/components/index.es";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { compose } from "redux";
import { arrayOf, bool, func, number, shape, string } from "prop-types";
import moment from "moment-timezone";
import * as i18n from "util/i18n";
import * as config from "util/config";
import { exportGenericTicketPdfWithSuccessMessage, getGeneralTicketItem } from "util/transaction";
import { parseFloatToAmountFormat } from "util/number";

import { push } from "react-router-redux";
import { resizableRoute } from "pages/_components/Resizable";
import { getPostLoginHomePath } from "util/v3UxConfig";
import { isUxDashboardImprovementsEnabled, useEmbeddedV3ShellLayoutSync } from "util/v3ShellLayoutSync";
import { resolveTipoCuentaFromAccounts } from "util/currencyExchangeFraudPayload";
import Head from "../../../_components/Head";
import Notification from "../../../_components/Notification";
import {
    actions as currencyExchangeActions,
    selectors as currencyExchangeSelectors,
} from "../../../../reducers/currencyExchange";
import { selectors as sessionSelectors } from "../../../../reducers/session";
import { actions as accountsActions, selectors as accountsSelectors } from "../../../../reducers/accounts";
import { actions as notificationActions } from "../../../../reducers/notification";
import {
    getLabel,
    formatRate,
    buildQuote,
    ARS_CURRENCY_CODES,
    USD_CURRENCY_CODES,
    CURRENCY_EXCHANGE_TRANSACTION_TOTAL_CURRENCY,
} from "./currencyExchangeUtils";

function CurrencyExchangePage({
    dispatch,
    userId,
    cotizacionCompra,
    cotizacionVenta,
    fetchingQuote,
    fetchingHistory,
    fetchingPreview,
    transactions,
    totalPages,
    pageNumber,
    accounts,
    enableBuy,
    enableSell,
    isDesktop,
    transactionTicket,
    fetchingDownload,
    previewError,
    embeddedInV3Shell,
}) {
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [formMode, setFormMode] = useState("buy");
    const [activeFilter, setActiveFilter] = useState("all");
    const quoteResolverRef = useRef(null);
    const prevFetchingPreviewRef = useRef(false);

    const currencyExchangeEnabled = config.getBoolean("frontend.foreign.currency.trading.enable", false);
    if (!currencyExchangeEnabled) {
        const uxDash = isUxDashboardImprovementsEnabled();
        dispatch(push(getPostLoginHomePath(uxDash)));
    }

    useEmbeddedV3ShellLayoutSync(dispatch, {
        embeddedInV3Shell,
        isDesktop,
        titleKey: "currencyExchange.title",
        goBackPath: "/v3/dashboard",
        scopes: ["currencyExchange"],
    });

    const handleBack = () => {
        if (embeddedInV3Shell) {
            dispatch(push("/v3/dashboard"));
            return;
        }
        dispatch(push("/desktop"));
    };

    useEffect(() => {
        dispatch(currencyExchangeActions.loadQuote());
        dispatch(currencyExchangeActions.loadHistory());
    }, [dispatch]);

    useEffect(() => {
        if (accounts.length === 0) {
            dispatch(accountsActions.listAccounts(userId));
        }
    }, [dispatch, userId, accounts.length]);

    useEffect(() => {
        if (quoteResolverRef.current && !fetchingQuote) {
            quoteResolverRef.current({ cotizacionCompra, cotizacionVenta });
            quoteResolverRef.current = null;
        }
    }, [fetchingQuote, cotizacionCompra, cotizacionVenta]);

    useEffect(() => {
        if (prevFetchingPreviewRef.current && !fetchingPreview && previewError) {
            setIsFormVisible(false);
        }
        prevFetchingPreviewRef.current = fetchingPreview;
    }, [fetchingPreview, previewError]);

    const buyQuote = useMemo(() => buildQuote(cotizacionVenta), [cotizacionVenta]);

    const sellQuote = useMemo(() => buildQuote(cotizacionCompra), [cotizacionCompra]);

    const accountOptions = useMemo(
        () =>
            accounts.map((account) => {
                let currency = i18n.get(`currency.label.${account.currency}`);
                if (ARS_CURRENCY_CODES.has(String(account.currency))) {
                    currency = "ARS";
                } else if (USD_CURRENCY_CODES.has(String(account.currency))) {
                    currency = "USD";
                }
                return {
                    value: account.number,
                    label: account.productAlias || account.shortLabel || account.number,
                    balance: account.balance,
                    currency,
                };
            }),
        [accounts],
    );

    const defaultArsAccountId = useMemo(() => {
        const arsAccounts = accounts.filter((account) => ARS_CURRENCY_CODES.has(String(account.currency)));
        if (arsAccounts.length === 1) {
            return arsAccounts[0].number;
        }
        if (arsAccounts.length > 1) {
            const favorite = arsAccounts.find((account) => account.favorite);
            return favorite?.number || arsAccounts[0]?.number || "";
        }
        return "";
    }, [accounts]);

    const defaultUsdAccountId = useMemo(() => {
        const usdAccounts = accounts.filter((account) => USD_CURRENCY_CODES.has(String(account.currency)));
        if (usdAccounts.length === 1) {
            return usdAccounts[0].number;
        }
        if (usdAccounts.length > 1) {
            const favorite = usdAccounts.find((account) => account.favorite);
            return favorite?.number || usdAccounts[0]?.number || "";
        }
        return "";
    }, [accounts]);

    const hasRequiredAccounts = useMemo(() => {
        const hasARS = accounts.some((account) => ARS_CURRENCY_CODES.has(String(account.currency)));
        const hasUSD = accounts.some((account) => USD_CURRENCY_CODES.has(String(account.currency)));
        return hasARS && hasUSD;
    }, [accounts]);

    const mappedTransactions = useMemo(
        () =>
            transactions.map((item) => {
                const tx = item.transaction || item;
                const data = tx.data || tx;
                const isBuy = data.tipoOperacion === "COMPRA" || tx.idActivity === "currencyExchange.confirm.buy";
                return {
                    id: data.solicitudCambioId,
                    idTransaction: tx.idTransaction,
                    creationDate: moment(tx.submitDateTime || data.dateTime).format("DD/MM/YYYY"),
                    activityId: isBuy ? "buy" : "sell",
                    exchangeRate: data.cotizacion,
                    totalAmount: isBuy ? data.importeCredito : data.importeDebito,
                    sourceCurrency: isBuy ? "ARS" : "USD",
                    targetCurrency: isBuy ? "USD" : "ARS",
                };
            }),
        [transactions],
    );

    const checkWorkingHours = useCallback(() => {
        const tradingDays = config.get("frontend.foreign.currency.trading.days", "2,3,4,5,6");
        const timeFrom = config.get("frontend.foreign.currency.trading.time.from", "10:00");
        const timeTo = config.get("frontend.foreign.currency.trading.time.to", "15:00");
        const timeZone = "America/Argentina/Buenos_Aires";
        const format = "HH:mm";

        const now = moment.tz(timeZone);
        const javaCalendarDay = now.day() + 1;
        const allowedDays = tradingDays.split(",").map((d) => Number.parseInt(d.trim(), 10));

        if (!allowedDays.includes(javaCalendarDay)) {
            return "currencyExchange.nonWorkingDay";
        }

        const beforeTime = moment.tz(timeFrom, format, timeZone);
        const afterTime = moment.tz(timeTo, format, timeZone);

        if (!now.isBetween(beforeTime, afterTime)) {
            return i18n.get("currencyExchange.outsideWorkingHours", null, {
                hourFrom: timeFrom,
                hourTo: timeTo,
            });
        }

        return null;
    }, []);

    const handleExchangeAction = useCallback(
        (mode) => {
            if (!hasRequiredAccounts) {
                dispatch(
                    notificationActions.showNotification(config.get("frontend.Request.NewAccount.Text"), "warning", [
                        "currencyExchange",
                    ]),
                );
                return;
            }

            const workingHoursError = checkWorkingHours();
            if (workingHoursError) {
                dispatch(notificationActions.showNotification(workingHoursError, "warning", ["currencyExchange"]));
                return;
            }

            setFormMode(mode);
            setIsFormVisible(true);
        },
        [hasRequiredAccounts, dispatch, checkWorkingHours],
    );

    const handleBuyClick = useCallback(() => handleExchangeAction("buy"), [handleExchangeAction]);

    const handleSellClick = useCallback(() => handleExchangeAction("sell"), [handleExchangeAction]);

    const handleFormClose = useCallback(() => {
        setIsFormVisible(false);
    }, []);

    const filterMap = useMemo(() => ({ all: "TODOS", buy: "COMPRA", sell: "VENTA" }), []);

    const handleFilterChange = useCallback(
        (filter) => {
            setActiveFilter(filter);
            dispatch(currencyExchangeActions.loadHistory(filterMap[filter], 1));
        },
        [dispatch, filterMap],
    );

    const handlePageChange = useCallback(
        (newPage) => {
            dispatch(currencyExchangeActions.loadHistory(filterMap[activeFilter], newPage, !isDesktop));
        },
        [dispatch, filterMap, activeFilter, isDesktop],
    );

    const handleCalculate = useCallback(
        (debitAmount, mode) =>
            new Promise((resolve) => {
                quoteResolverRef.current = (quoteData) => {
                    const rate = mode === "buy" ? quoteData.cotizacionVenta : quoteData.cotizacionCompra;

                    if (!rate) {
                        resolve({ creditAmount: 0, exchangeRate: 0 });
                        return;
                    }

                    const creditAmount =
                        mode === "buy"
                            ? Number((debitAmount / rate).toFixed(2))
                            : Number((debitAmount * rate).toFixed(2));

                    resolve({ creditAmount, exchangeRate: formatRate(rate) });
                };
                dispatch(currencyExchangeActions.loadQuote());
            }),
        [dispatch],
    );

    const handleContinue = useCallback(
        (data) => {
            const tipoOperacion = data.mode === "buy" ? "COMPRA" : "VENTA";
            const tipoCuentaDebito = resolveTipoCuentaFromAccounts(data.debitAccountId, undefined, accounts);
            const tipoCuentaCredito = resolveTipoCuentaFromAccounts(data.creditAccountId, undefined, accounts);
            dispatch(
                currencyExchangeActions.createPreview({
                    tipoOperacion,
                    importeDebito: data.debitAmount,
                    cuentaDebito: data.debitAccountId,
                    cuentaCredito: data.creditAccountId,
                    tipoCuentaDebito,
                    tipoCuentaCredito,
                }),
            );
        },
        [dispatch, accounts],
    );

    const handleFormModeChange = useCallback((mode) => {
        setFormMode(mode);
    }, []);

    const paginationProps = useMemo(
        () => ({
            totalPages,
            pageNumber,
            action: handlePageChange,
        }),
        [totalPages, pageNumber, handlePageChange],
    );

    const handleTransactionClick = useCallback(
        (transactionId) => {
            dispatch(currencyExchangeActions.readTransaction(transactionId));
        },
        [dispatch],
    );

    const handleTransactionTicketClose = useCallback(() => {
        dispatch(currencyExchangeActions.clearTransactionTicket());
    }, [dispatch]);

    const formatAmountWithCurrency = useCallback((amount, currency) => {
        const currencyLabel = i18n.get(`currency.label.${currency}`);
        return `${currencyLabel} ${parseFloatToAmountFormat(amount)}`;
    }, []);

    const generateTicketBody = useCallback(
        (ticket) => {
            if (!ticket) {
                return [];
            }
            return [
                getGeneralTicketItem("currencyExchange.ui.ticketDateTime", ticket.dateTime, "string"),
                getGeneralTicketItem("currencyExchange.ui.ticketVoucher", ticket.voucherNumber, "string"),
                getGeneralTicketItem("currencyExchange.ui.ticketOperationNumber", ticket.operationNumber, "string"),
                getGeneralTicketItem("currencyExchange.ui.ticketDebitAccount", ticket.debitAccount, "string"),
                getGeneralTicketItem("currencyExchange.ui.ticketCreditAccount", ticket.creditAccount, "string"),
                getGeneralTicketItem(
                    "currencyExchange.ui.debitAmount",
                    formatAmountWithCurrency(ticket.debitAmount, ticket.debitCurrency),
                    "string",
                ),
                getGeneralTicketItem(
                    "currencyExchange.ui.creditAmount",
                    formatAmountWithCurrency(ticket.creditAmount, ticket.creditCurrency),
                    "string",
                ),
                getGeneralTicketItem(
                    "currencyExchange.ticket.exchangeRate",
                    formatAmountWithCurrency(ticket.exchangeRate, "ARS"),
                    "string",
                ),
            ];
        },
        [formatAmountWithCurrency],
    );

    const handleDownloadTicket = useCallback(async () => {
        if (!transactionTicket) {
            return;
        }
        dispatch(currencyExchangeActions.fetchDownloadStart());
        try {
            const ticketTitle =
                transactionTicket.type === "buy"
                    ? i18n.get("currencyExchange.ui.ticketTitleBuy")
                    : i18n.get("currencyExchange.ui.ticketTitleSell");
            const ticketData = {
                ticketName: ticketTitle,
                ticketBody: [...generateTicketBody(transactionTicket)],
            };
            await exportGenericTicketPdfWithSuccessMessage(ticketData, dispatch, i18n.get("global.download.success"));
        } finally {
            dispatch(currencyExchangeActions.fetchDownloadEnd());
        }
    }, [transactionTicket, generateTicketBody, dispatch]);

    return (
        <>
            {!embeddedInV3Shell && <Notification scopeToShow="currencyExchange" />}
            <Box className="admin-detail-head" px={isDesktop ? 40 : 24}>
                {isDesktop && <Head onBack={handleBack} />}
                {!(embeddedInV3Shell && !isDesktop) && (
                    <Head
                        title="currencyExchange.title"
                        onBack={!isDesktop && handleBack}
                        titleClassName={
                            isDesktop
                                ? "my-3 align-center"
                                : "header-mobile-title-background-blue overflow-visible white-space-normal align-center"
                        }
                        headerClassName={isDesktop ? "" : "blue-main-header-mobile"}
                        {...(isDesktop && {
                            messageTooltip: config.get("frontend.foreign.currency.trading.text"),
                            hintText: config.get("frontend.foreign.currency.trading.text"),
                            hintClassName: "max-width-37rem",
                        })}
                        alwaysShowMessage={isDesktop}
                    />
                )}

                <CurrencyExchange
                    enableBuy={enableBuy}
                    enableSell={enableSell}
                    transactionTotalCurrency={CURRENCY_EXCHANGE_TRANSACTION_TOTAL_CURRENCY}
                    buyQuote={buyQuote}
                    sellQuote={sellQuote}
                    loadingQuotes={fetchingQuote}
                    transactions={mappedTransactions}
                    loading={fetchingHistory}
                    activeFilter={activeFilter}
                    onFilterChange={handleFilterChange}
                    onBuyClick={handleBuyClick}
                    onSellClick={handleSellClick}
                    isFormVisible={isFormVisible}
                    initialFormMode={formMode}
                    onFormModeChange={handleFormModeChange}
                    onFormClose={handleFormClose}
                    onCalculate={handleCalculate}
                    loadingContinue={fetchingPreview}
                    onContinue={handleContinue}
                    accounts={accountOptions}
                    getLabels={getLabel}
                    defaultArsAccountId={defaultArsAccountId}
                    defaultUsdAccountId={defaultUsdAccountId}
                    hintText={config.get("frontend.foreign.currency.trading.text")}
                    paginationProps={paginationProps}
                    onTransactionClick={handleTransactionClick}
                    transactionTicket={transactionTicket}
                    onTransactionTicketClose={handleTransactionTicketClose}
                    onDownloadTicket={handleDownloadTicket}
                    isFetchingExport={fetchingDownload}
                    isInfiniteScroll={!isDesktop}
                />
            </Box>
        </>
    );
}

CurrencyExchangePage.propTypes = {
    dispatch: func.isRequired,
    userId: string,
    cotizacionCompra: number,
    cotizacionVenta: number,
    fetchingQuote: bool,
    fetchingHistory: bool,
    fetchingPreview: bool,
    transactions: arrayOf(shape({})),
    totalPages: number,
    pageNumber: number,
    accounts: arrayOf(shape({})),
    enableBuy: bool,
    enableSell: bool,
    isDesktop: bool.isRequired,
    transactionTicket: shape({}),
    fetchingDownload: bool,
    previewError: string,
    embeddedInV3Shell: bool,
};

CurrencyExchangePage.defaultProps = {
    userId: "",
    cotizacionCompra: null,
    cotizacionVenta: null,
    fetchingQuote: false,
    fetchingHistory: false,
    fetchingPreview: false,
    transactions: [],
    totalPages: 0,
    pageNumber: 1,
    accounts: [],
    enableBuy: false,
    enableSell: false,
    transactionTicket: null,
    fetchingDownload: false,
    previewError: null,
    embeddedInV3Shell: false,
};

const mapStateToProps = (state) => {
    const activeEnvironment = sessionSelectors.getActiveEnvironment(state);
    return {
        userId: sessionSelectors.getUser(state)?.userId || "",
        cotizacionCompra: currencyExchangeSelectors.getCotizacionCompra(state),
        cotizacionVenta: currencyExchangeSelectors.getCotizacionVenta(state),
        fetchingQuote: currencyExchangeSelectors.getFetchingQuote(state),
        fetchingHistory: currencyExchangeSelectors.getFetchingHistory(state),
        fetchingPreview: currencyExchangeSelectors.getFetchingPreview(state),
        transactions: currencyExchangeSelectors.getTransactions(state),
        totalPages: currencyExchangeSelectors.getTotalPages(state),
        pageNumber: currencyExchangeSelectors.getPageNumber(state),
        accounts: accountsSelectors.getAccounts(state),
        enableBuy: !!activeEnvironment?.permissions?.["investment.currencyExchange.buy"],
        enableSell: !!activeEnvironment?.permissions?.["investment.currencyExchange.sell"],
        transactionTicket: currencyExchangeSelectors.getTransactionTicket(state),
        fetchingDownload: currencyExchangeSelectors.getFetchingDownload(state),
        previewError: currencyExchangeSelectors.getError(state),
    };
};

export default compose(withRouter, connect(mapStateToProps), resizableRoute)(CurrencyExchangePage);
